import { useCallback, useEffect, useRef, useState } from "react";

import {
  ApiFailure,
  api,
  subscribe,
  type ChatMessage,
  type Conversation,
  type Page,
  type PublicUser,
} from "../lib/api";
import { Badge, Button, Card, Empty, ErrorNote, Skeleton, inputClass, relativeTime } from "../components/ui";

/**
 * The chat viewer.
 *
 * Two panes: the conversation list, and the thread. Both are fed by the same
 * SSE stream, so a DM arriving in Discord lands here without a refresh — which
 * is the whole point of the screen.
 */
export function Conversations({ user }: { user: PublicUser }) {
  const [list, setList] = useState<Conversation[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canReply = user.role === "owner" || user.role === "admin" || user.role === "operator";

  const loadList = useCallback(async () => {
    try {
      const page = await api.get<Page<Conversation>>("/conversations?limit=50");
      setList(page.items);
      setError(null);
      return page.items;
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not load conversations.");
      return [];
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(
    () =>
      subscribe(["conversation.message_received", "conversation.message_sent"], () => {
        void loadList();
      }),
    [loadList],
  );

  return (
    <div className="grid gap-6">
      <header>
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Chats</h1>
        <p className="mt-1 text-[13px] text-[var(--muted)]">
          Every conversation the bot is part of, live.
        </p>
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <Card padded={false} className="overflow-hidden">
          {list === null ? (
            <div className="grid gap-2 p-4">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <Empty
              title="No conversations yet"
              body="A thread appears here the first time someone sends the bot a direct message."
            />
          ) : (
            <ul className="max-h-[560px] overflow-y-auto">
              {list.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelected(c.id)}
                    className={
                      "w-full border-b border-[var(--border)] px-4 py-3 text-left transition-colors duration-150 " +
                      (selected === c.id
                        ? "bg-[var(--brand-soft)]"
                        : "hover:bg-[var(--surface-2)]")
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[14px] font-medium">
                        {c.counterpartName ?? c.title}
                      </span>
                      {c.unreadCount > 0 && <Badge tone="brand">{c.unreadCount}</Badge>}
                    </div>
                    <div className="mt-0.5 truncate text-[12px] text-[var(--faint)]">
                      {c.lastMessagePreview ?? "No messages"}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-[var(--faint)]">
                      <Badge tone="neutral">{c.kind === "dm" ? "DM" : "channel"}</Badge>
                      <span>{relativeTime(c.lastMessageAt)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {selected ? (
          <Thread
            conversationId={selected}
            canReply={canReply}
            onActivity={() => void loadList()}
          />
        ) : (
          <Card padded={false}>
            <Empty
              title="Pick a conversation"
              body="Choose a thread on the left to read it, and to reply as the bot."
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function Thread({
  conversationId,
  canReply,
  onActivity,
}: {
  conversationId: string;
  canReply: boolean;
  onActivity: () => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[] | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const page = await api.get<Page<ChatMessage>>(`/conversations/${conversationId}/messages?limit=50`);
    // The API returns newest-first for paging; a chat reads oldest-first.
    setMessages([...page.items].reverse());
    await api.post(`/conversations/${conversationId}/read`).catch(() => undefined);
  }, [conversationId]);

  useEffect(() => {
    setMessages(null);
    void load().catch(() => setError("Could not load this conversation."));
  }, [load]);

  useEffect(
    () =>
      subscribe(["conversation.message_received", "conversation.message_sent"], (event) => {
        const data = event.data as { conversation?: { id?: string } };
        if (data?.conversation?.id === conversationId) void load();
      }),
    [conversationId, load],
  );

  // Stick to the bottom as messages arrive, the way every chat client does.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  async function send() {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    setError(null);
    try {
      await api.post(`/conversations/${conversationId}/messages`, { content });
      setDraft("");
      await load();
      onActivity();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not send that.");
    } finally {
      setSending(false);
    }
  }

  return (
    <Card padded={false} className="flex h-[560px] flex-col">
      <div className="flex-1 overflow-y-auto p-5">
        {messages === null ? (
          <div className="grid gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className={i % 2 ? "h-12 w-2/3 justify-self-end" : "h-12 w-2/3"} />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <Empty title="Nothing said yet" body="This conversation has no messages." />
        ) : (
          <div className="grid gap-3">
            {messages.map((m) => (
              <Bubble key={m.id} message={m} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {canReply && (
        <div className="border-t border-[var(--border)] p-3">
          {error && (
            <div className="mb-2">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}
          <div className="flex gap-2">
            <input
              className={inputClass}
              placeholder="Reply as the bot…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              maxLength={2000}
            />
            <Button variant="primary" onClick={() => void send()} disabled={sending || !draft.trim()}>
              {sending ? "Sending…" : "Send"}
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-[var(--faint)]">
            This is sent to the person in Discord, as the bot.
          </p>
        </div>
      )}
    </Card>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  // A system note is the bot narrating an event, not talking. It reads as a
  // centred line rather than a bubble so it cannot be mistaken for something
  // the recipient was actually sent.
  if (message.authorKind === "system") {
    return (
      <div className="my-1 text-center text-[12px] text-[var(--faint)]">
        {message.content} · {relativeTime(message.createdAt)}
      </div>
    );
  }

  const outbound = message.direction === "outbound";

  return (
    <div className={outbound ? "flex justify-end" : "flex justify-start"}>
      <div className="max-w-[75%]">
        <div
          className={
            "rounded-[14px] px-3.5 py-2.5 text-[14px] leading-relaxed " +
            (outbound
              ? "bg-[var(--brand)] text-[var(--brand-ink)]"
              : "bg-[var(--surface-2)] text-[var(--foreground)]")
          }
        >
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <div
          className={
            "mt-1 text-[11px] text-[var(--faint)] " + (outbound ? "text-right" : "text-left")
          }
        >
          {message.authorName} · {relativeTime(message.createdAt)}
        </div>
      </div>
    </div>
  );
}
